// track/hooks/useDataFetching.ts
import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  Firestore,
} from 'firebase/firestore';
import { Transaction, TransactionMethod, Client, Vendor } from '../types';

export const useDataFetching = (
  db: Firestore,
  selectedOrganizationId: string,
  userPermissions: string[]
) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [transactionMethods, setTransactionMethods] = useState<TransactionMethod[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const canRead = useCallback(() => {
    return userPermissions.some(permission =>
      permission.startsWith(`organizations_${selectedOrganizationId}`)
    );
  }, [selectedOrganizationId, userPermissions]);

  const fetchAllData = useCallback(async () => {
    if (!selectedOrganizationId || !canRead()) {
      setTransactions([]);
      setTransactionMethods([]);
      setClients([]);
      setVendors([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch all transactions for the organization
      const transactionsQuery = query(
        collection(db, 'transactions'),
        where('organizationId', '==', selectedOrganizationId)
      );
      const transactionSnapshot = await getDocs(transactionsQuery);
      const allTransactions = transactionSnapshot.docs.flatMap(doc => {
        const data = doc.data();
        return data.transactions ? data.transactions.map((t: any) => ({ ...t, id: doc.id })) : [];
      }) as Transaction[];
      setTransactions(allTransactions);

      // Fetch transaction methods
      const methodsQuery = query(
        collection(db, 'organizations', selectedOrganizationId, 'transactionMethods'),
        where('enabled', '==', true)
      );
      const methodsSnapshot = await getDocs(methodsQuery);
      const methodData = methodsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as TransactionMethod[];
      setTransactionMethods(methodData);

      // Fetch clients
      const clientsQuery = query(
        collection(db, 'organizations', selectedOrganizationId, 'clients'),
        orderBy('order', 'asc')
      );
      const clientsSnapshot = await getDocs(clientsQuery);
      const clientData = clientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Client[];
      setClients(clientData);

      // Fetch vendors
      const vendorsQuery = query(
        collection(db, 'organizations', selectedOrganizationId, 'vendors'),
        orderBy('order', 'asc')
      );
      const vendorsSnapshot = await getDocs(vendorsQuery);
      const vendorData = vendorsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Vendor[];
      setVendors(vendorData);

    } catch (err: any) {
      console.error("Error fetching data:", err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [db, selectedOrganizationId, canRead]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  return {
    transactions,
    transactionMethods,
    clients,
    vendors,
    loading,
    error,
    reloadData: fetchAllData,
  };
};